import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Theme detection
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      setIsDark(true);
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleDarkMode = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    if (newIsDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const menuItems = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Resources', path: '/resources' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' }
  ];


  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 dark:bg-secondary-900/95 backdrop-blur-md shadow-2xl py-2'
          : 'bg-white/70 dark:bg-secondary-900/70 backdrop-blur-sm py-3'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group relative z-50">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-500 shadow-xl shadow-primary-500/20 overflow-hidden">
                <img
                  src="https://res.cloudinary.com/dzj1fusx1/image/upload/v1768166080/2999d25d-09bd-49c1-8af2-a0c12acc1734.png"
                  alt="Kehosa Institute for Strategic Studies"
                  className="w-full h-full object-contain"
                />
            </div>
            <div className="flex flex-col">
              <h1 className="text-sm sm:text-base lg:text-lg font-black text-secondary-900 dark:text-white tracking-tight leading-none">
                <span className="hidden sm:inline">KEHOSA </span><span className="text-primary-600">INSTITUTE</span>
                <span className="hidden xl:inline"> FOR <span className="text-primary-600">STRATEGIC</span> STUDIES</span>
              </h1>
              <p className="text-[5px] sm:text-[8px] lg:text-[9px] uppercase tracking-[0.1em] sm:tracking-[0.15em] lg:tracking-[0.2em] font-bold text-secondary-500 dark:text-secondary-400 -mt-0.10">
                <span className="hidden sm:inline">KISS • </span>Strategy Is the Foundation of Success
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {menuItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative font-semibold text-sm uppercase tracking-wide transition-all duration-300 px-3 py-2 rounded-lg ${
                  isActive(item.path)
                    ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20'
                    : 'text-secondary-700 dark:text-secondary-300 hover:text-primary-600 dark:hover:text-primary-500 hover:bg-secondary-50 dark:hover:bg-secondary-800'
                }`}
              >
                {item.name}
                {isActive(item.path) && (
                  <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary-600 rounded-full mt-1"></span>
                )}
              </Link>
            ))}
          </nav>

          {/* Action Buttons */}
          <div className="flex items-center space-x-4 relative z-50">
          

            <Link
              to="/contact"
              className="hidden md:flex items-center px-4 lg:px-6 py-2 lg:py-3 bg-secondary-900 dark:bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-600 dark:hover:bg-primary-500 transform hover:-translate-y-1 transition-all duration-300 shadow-xl shadow-secondary-900/10 dark:shadow-primary-900/20 text-xs uppercase tracking-wide"
            >
              Register for Strategy101
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2 rounded-xl bg-secondary-100 dark:bg-secondary-800 text-secondary-900 dark:text-white hover:text-primary-600 transition-colors focus:outline-none"
              onClick={toggleMenu}
            >
              <i className={`bi ${isMenuOpen ? 'bi-x-lg' : 'bi-list'} text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40 bg-white dark:bg-secondary-900 pt-16 px-4 animate-fade-in custom-scrollbar overflow-y-auto">
            <nav className="flex flex-col space-y-2 pb-4">
              {menuItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center py-4 px-4 font-semibold text-lg transition-all rounded-xl ${
                    isActive(item.path)
                      ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600'
                      : 'text-secondary-800 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-800'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-6 mt-4">
                <Link
                  to="/contact"
                  className="w-full flex justify-center py-3 bg-primary-600 text-white font-bold text-base rounded-xl shadow-2xl shadow-primary-500/30 hover:bg-primary-700 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Register Now
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
  